import React, { useState, useEffect, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import useRoadmapProgress from '../hooks/useRoadmapProgress';
import { getLevel, levels } from '../data/roadmapData';
import Digicode from '../components/Roadmap/Digicode';
import LevelMap from '../components/Roadmap/LevelMap';
import ItemScreen from '../components/Roadmap/ItemScreen';
import Celebration from '../components/Roadmap/Celebration';
import '../components/Roadmap/roadmap.css';

const UNLOCK_KEY = 'roadmap-unlocked-v1';

export default function Roadmap() {
  const [unlocked, setUnlocked] = useState(
    () => localStorage.getItem(UNLOCK_KEY) === '1'
  );
  const progress = useRoadmapProgress();
  const {
    setFieldAnswer,
    validateItem,
    isLevelComplete,
    firstPendingItem,
    answers,
    globalProgress,
  } = progress;

  // view: { screen: 'map' } | { screen: 'level', levelId, index } | { screen: 'celebrate', levelId }
  const [view, setView] = useState({ screen: 'map' });

  const handleUnlock = useCallback(() => {
    localStorage.setItem(UNLOCK_KEY, '1');
    setUnlocked(true);
  }, []);

  const openLevel = useCallback(
    (levelId) => {
      const level = getLevel(levelId);
      if (isLevelComplete(levelId)) {
        setView({ screen: 'celebrate', levelId });
        return;
      }
      const pending = firstPendingItem(levelId);
      const index = pending
        ? level.items.findIndex((it) => it.id === pending.id)
        : 0;
      setView({ screen: 'level', levelId, index });
    },
    [isLevelComplete, firstPendingItem]
  );

  // Détecte la complétion d'un niveau pendant qu'on est dedans → célébration.
  useEffect(() => {
    if (view.screen === 'level' && isLevelComplete(view.levelId)) {
      const t = setTimeout(() => {
        setView({ screen: 'celebrate', levelId: view.levelId });
      }, 650); // laisse les confettis de l'item respirer
      return () => clearTimeout(t);
    }
  }, [view, isLevelComplete]);

  const goMap = useCallback(() => setView({ screen: 'map' }), []);

  const advance = useCallback(() => {
    setView((v) => {
      if (v.screen !== 'level') return v;
      const level = getLevel(v.levelId);
      const nextIdx = Math.min(v.index + 1, level.items.length - 1);
      return { ...v, index: nextIdx };
    });
  }, []);

  const backFromItem = useCallback(() => {
    setView((v) => {
      if (v.screen !== 'level') return { screen: 'map' };
      if (v.index > 0) return { ...v, index: v.index - 1 };
      return { screen: 'map' };
    });
  }, []);

  if (!unlocked) {
    return <Digicode onUnlock={handleUnlock} />;
  }

  let content = null;

  if (view.screen === 'map') {
    content = (
      <LevelMap key="map" progress={progress} onOpenLevel={openLevel} />
    );
  } else if (view.screen === 'level') {
    const level = getLevel(view.levelId);
    const item = level.items[view.index];
    content = (
      <ItemScreen
        key={`item-${item.id}`}
        level={level}
        item={item}
        index={view.index}
        total={level.items.length}
        answers={answers}
        onChangeField={(fieldKey, value) =>
          setFieldAnswer(item.id, fieldKey, value)
        }
        onValidate={() => {
          validateItem(item.id);
          advance();
        }}
        onBack={backFromItem}
      />
    );
  } else if (view.screen === 'celebrate') {
    const level = getLevel(view.levelId);
    const nextLevel = levels.find((l) => l.id === view.levelId + 1) || null;
    content = (
      <Celebration
        key={`celebrate-${level.id}`}
        level={level}
        nextLevel={nextLevel}
        answers={answers}
        globalRatio={globalProgress.ratio}
        onContinue={() => nextLevel && openLevel(nextLevel.id)}
        onHome={goMap}
      />
    );
  }

  return (
    <div className="rm-app">
      <AnimatePresence mode="wait">
        <motion.div
          key={view.screen + (view.levelId || '')}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="rm-app-inner"
        >
          {content}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
